/**
 * playbackTransport.js
 *
 * Wire transport for the storage-backed playback contract:
 *   playback_request (viewer -> device)  ->  playback_ack  ->  playback_done
 * See ../../../../../docs/PLAYBACK-HANDOFF.md §4.2, §4.4, §6.3.
 *
 * THIS IS OPTION B (owner decision 2026-09-12: "B trước, A sau"). The SDK's
 * public runtime (`@vgps/livekit_web` core/runtime/types.ts
 * VgpsLivekitWebRuntime) has no generic send/subscribe — `SignalingClient`
 * (which already has a usable `send()` / `request({responseType,
 * timeoutMs})`) is a private field of the runtime impl. The two lookalike
 * primitives the runtime DOES expose are both wrong for this feature:
 *   - openPlaybackSession(): scrubs a historic time RANGE through the live
 *     relay/WebRTC pipeline (core/session/session.ts `PlaybackSession`) —
 *     an unrelated, pre-existing feature that happens to share the name.
 *   - openClipSession(): opens its OWN dedicated signaling WS + WebRTC
 *     DataChannel expecting fragmented-MP4 over the channel
 *     (core/session/clip_session.ts) — this is the transport
 *     `usePlaybackChannel`/`handlePlayClip` used to call; the device has no
 *     handler for it (handoff §6.1 point 1).
 * So this module opens a second, independent WebSocket to the same
 * `REACT_APP_LIVEKIT_SIGNALING_URL` the SDK connects to, speaking only the
 * plain `playback_*` JSON messages, exactly as the firmware team's mock FE
 * and every owner validation run have done.
 *
 * SEAM for Option A (later): every caller goes through requestPlayback() /
 * subscribePlayback() / connectionState() below — none of them know a raw
 * WebSocket exists. When `@vgps/livekit_web` ships
 * `runtime.sendMessage()`/`runtime.onMessage()` (or dedicated
 * requestPlayback()/onPlaybackEvent() per handoff §6.3 option A), only the
 * body of getConnection()/requestPlayback() needs to change to delegate to
 * the runtime; nothing in useCardPlayback.js or above needs to move.
 *
 * Correlation: playback_ack/playback_done carry NO request_id (unlike
 * list_events/event_list, which do) and NO `camera` field (handoff §6.3,
 * §4.2). The device also serves only ONE playback transfer at a time, so at
 * most one playback_request is ever outstanding per device_serial. This
 * module does no correlation beyond device_serial de-multiplexing — matching
 * an ack/done to "my" card (by slot_id/ts_us/segment_uid) is useCardPlayback's
 * job (handoff calls this `routeReplyToCard()` in the mock FE, `:2850`).
 */

const CLIENT_ID_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';
function randomClientId(len = 10) {
  let id = '';
  for (let i = 0; i < len; i++) {
    id += CLIENT_ID_CHARS[Math.floor(Math.random() * CLIENT_ID_CHARS.length)];
  }
  return id;
}

function currentAccessToken() {
  try {
    return localStorage.getItem('access-token') || '';
  } catch (_) {
    return '';
  }
}

function signalingBaseUrl() {
  const url = process.env.REACT_APP_LIVEKIT_SIGNALING_URL;
  return url ? url.replace(/\/$/, '') : '';
}

const CONNECT_TIMEOUT_MS = 8000;

// One raw socket per browser tab. A single connection can send
// playback_request for any device_serial the JWT scopes allow, so there is
// no reason to open one per device; message routing (below) is keyed on
// device_serial regardless.
let shared = null; // { ws, clientId, state, listeners: Set<fn>, openPromise }

function teardown(conn, reason) {
  if (shared !== conn) return;
  shared = null;
  for (const fn of conn.listeners) {
    try {
      fn({ type: '__transport_closed__', reason });
    } catch (err) {
      console.error('[playbackTransport] listener threw on teardown', err);
    }
  }
}

function openConnection() {
  const clientId = randomClientId();
  const base = signalingBaseUrl();
  const token = currentAccessToken();
  const url = `${base}/${clientId}?token=${encodeURIComponent(token)}`;

  const conn = {
    ws: null,
    clientId,
    state: 'connecting',
    listeners: new Set(),
    openPromise: null,
  };

  conn.openPromise = new Promise((resolve, reject) => {
    let settled = false;
    const settle = (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (err) reject(err);
      else resolve();
    };

    const timer = setTimeout(() => {
      settle(new Error('playback transport: signaling connect timed out'));
      try { ws.close(); } catch (_) { /* ignore */ }
    }, CONNECT_TIMEOUT_MS);

    let ws;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      clearTimeout(timer);
      reject(err);
      return;
    }
    conn.ws = ws;

    ws.onopen = () => {
      conn.state = 'connected';
      settle();
    };
    ws.onerror = () => {
      conn.state = 'error';
      settle(new Error('playback transport: signaling connection failed'));
    };
    ws.onclose = (evt) => {
      settle(new Error('playback transport: signaling closed before connecting'));
      conn.state = 'closed';
      teardown(conn, evt && evt.reason);
    };
    ws.onmessage = (evt) => {
      if (typeof evt.data !== 'string') return;
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch (_) {
        return;
      }
      if (msg.type !== 'playback_ack' && msg.type !== 'playback_done') return;
      // Snapshot BEFORE dispatching. A listener's handleAck can synchronously
      // touch shared context state (playbackDeviceContext's claimUploading/
      // releaseUploading), which -- outside React's own event system -- React
      // 16 applies immediately, cascading a resubscribe (delete+add on THIS
      // Set) for every card still inside this same dispatch. Iterating the
      // live Set directly meant a just-re-added entry for the SAME card got
      // revisited by this SAME iterator (Set semantics: an element added
      // during iteration IS visited if not yet passed), which re-triggered
      // its own resubscribe again, forever -- turning ONE real playback_ack
      // into an unbounded loop that never let the decode pipeline survive
      // more than an instant (verified with Playwright: a single ack produced
      // 300+ handler calls, all synchronously nested in this one dispatch).
      // A plain array copy is immune: later Set mutations cannot affect it.
      for (const fn of Array.from(conn.listeners)) {
        try {
          fn(msg);
        } catch (err) {
          console.error('[playbackTransport] listener threw', err);
        }
      }
    };
  });

  shared = conn;
  return conn;
}

function getConnection() {
  if (shared && (shared.state === 'connected' || shared.state === 'connecting')) {
    return shared;
  }
  return openConnection();
}

/**
 * Subscribe to every playback_ack/playback_done this tab's transport socket
 * receives (all devices, unfiltered — callers narrow by device_serial and
 * slot_id/segment_uid themselves). Also delivers a single synthetic
 * `{type:'__transport_closed__'}` if/when the socket dies, so a card stuck
 * in `requesting`/`buffering` (nothing will ever answer it now) can recover
 * instead of spinning forever — the E-S2-44 stuck-upload watchdog, applied
 * to a dead socket instead of a dead timer.
 *
 * Opens the connection as a side effect if none is live, so the socket
 * exists before the first requestPlayback() call and acks are never missed
 * to a subscribe-after-send race.
 */
export function subscribePlayback(handler) {
  const conn = getConnection();
  conn.listeners.add(handler);
  return () => {
    conn.listeners.delete(handler);
  };
}

/**
 * Send a playback_request. Resolves once the message is written to the
 * socket (NOT once acked — await the ack via subscribePlayback()).
 *
 * @param {object} req
 * @param {string} req.deviceSerial
 * @param {1|2} req.camera        1-based on the wire (handoff §4.2) — pass
 *                                 clip.cameraNum, never a 0-based index.
 * @param {number} req.slotId
 * @param {number} req.tsUs       required in practice: slot_id alone is a
 *                                 reusable ring index (§4.2).
 */
export async function requestPlayback({ deviceSerial, camera, slotId, tsUs }) {
  if (!deviceSerial) throw new Error('playbackTransport.requestPlayback: deviceSerial is required');
  if (camera !== 1 && camera !== 2) {
    throw new Error(`playbackTransport.requestPlayback: camera must be 1 or 2 (wire is 1-based), got ${camera}`);
  }
  if (tsUs == null) {
    throw new Error('playbackTransport.requestPlayback: ts_us is required (slot_id alone is a reusable ring index)');
  }
  const conn = getConnection();
  await conn.openPromise;
  conn.ws.send(JSON.stringify({
    type: 'playback_request',
    device_serial: deviceSerial,
    camera,
    slot_id: slotId,
    ts_us: tsUs,
  }));
}

export function connectionState() {
  return shared ? shared.state : 'disconnected';
}

/** cardKey — mirrors mock FE `cardKey()`. Cards MUST be keyed by
 * (cameraNum, slotId) together, never slot_id alone (handoff §9 trap #1:
 * "CAM 2 overwrote CAM 1 on ~100 of 200 events, replies routed to the wrong
 * tile" — slot_id is a ring index reused across cameras). */
export function cardKey(cameraNum, slotId) {
  return `${cameraNum}:${slotId}`;
}

/** camNumFromSegmentUid — mirrors mock FE (`:683`). segment_uid shape is
 * `<serial>_<camIndex0based>_<seq>_<ts_us>`; playback_ack/playback_done
 * carry no `camera` field, so the reply's camera must be parsed out of this
 * string. Returns a 1-based camera number to match the wire's
 * `playback_request.camera`, or null if the uid doesn't parse. */
export function camNumFromSegmentUid(segmentUid) {
  if (typeof segmentUid !== 'string') return null;
  const parts = segmentUid.split('_');
  if (parts.length < 4) return null;
  const idx0 = Number(parts[1]);
  if (!Number.isFinite(idx0)) return null;
  return idx0 + 1;
}
